import { create } from "zustand";
import {
  ChangelogComment,
  HttpChangelogService,
} from "../Services/http.clients/http.changelog.client";
import { textToCommentDoc } from "../Helper/ChangelogComments";

interface ChangelogCommentsState {
  // The changelog the comments below belong to; a late answer for another
  // changelog is dropped.
  changelogId?: string;
  comments: ChangelogComment[];
  // False when the portal API refused the request (a plan without changelogs,
  // the portal's changelogs tab closed to this visitor): the section hides.
  available: boolean;
  loading: boolean;
  sending: boolean;
  loadComments: (changelogId: string) => Promise<void>;
  addComment: (changelogId: string, text: string) => Promise<boolean>;
}

export const useChangelogCommentsStore = create<ChangelogCommentsState>()(
  (set, get) => ({
    changelogId: undefined,
    comments: [],
    available: true,
    loading: false,
    sending: false,
    loadComments: async (changelogId: string) => {
      if (get().changelogId !== changelogId) {
        set({ changelogId, comments: [], available: true });
      }
      set({ loading: true });
      try {
        const comments =
          await HttpChangelogService.getInstance().getComments(changelogId);
        if (get().changelogId === changelogId) {
          set({ comments, available: true, loading: false });
        }
      } catch (_) {
        if (get().changelogId === changelogId) {
          set({ comments: [], available: false, loading: false });
        }
      }
    },
    addComment: async (changelogId: string, text: string) => {
      if (!text.trim() || get().sending) return false;
      set({ sending: true });
      try {
        await HttpChangelogService.getInstance().addComment(
          changelogId,
          textToCommentDoc(text),
        );
        set({ sending: false });
        // Reload rather than append: the server decides what this visitor
        // may read, and returns names and team replies as everyone sees them.
        await get().loadComments(changelogId);
        return true;
      } catch (_) {
        set({ sending: false });
        return false;
      }
    },
  }),
);
